import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import Page from 'modules/app/components/Page';
import Skeleton from 'components/shared/Skeleton';
import PageContainer from 'components/shared/PageContainer';
import clsx from 'clsx';
import useAssets from 'sdk/hooks/useAssets';
import ChartPreview from 'modules/feed/components/Feed/ChartPreview';
import useAssetEvents from 'sdk/hooks/useAssetEvents';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';
import { formatAssetStat } from 'sdk/utils/formatValues';
import styles from './PopularStocks.module.scss';

function Asset({ asset }: { asset: any }) {
  const assetEventData = useAssetEvents(
    asset?.id,
    asset?.category?.isNFT ? '24H' : '1D',
  )

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const oldPrice = asset?.quote?.previous_close || 0
  const lastPrice = asset?.latest_price?.price || 0
  const delta = Math.round(100 * (lastPrice - oldPrice)) / 100
  const deltaPercent = Math.round(10000 * (oldPrice ? (delta / oldPrice) : 0)) / 100
  // eslint-disable-next-line no-unsafe-optional-chaining
  const marketCup = asset?.stats_finance?.['Stock Outstanding'] * asset?.latest_price?.price

  return (
    <tr>
      <td className={styles.nameBlockTd}>
        <Link to={`/asset/${asset?.id}`} className={styles.nameBlock}>
          {asset?.icon ? (
            <img src={asset?.icon} alt={asset?.name} className={styles.nameBlockLogo} />
          ) : null}
          <div className={styles.name}>
            {asset.ticker}
            {' '}
            <span>{`(${asset.name})`}</span>
          </div>
        </Link>
      </td>
      <td>
        <ChartPreview
          className={styles.catGraphDemo}
          data={assetEventData?.data || []}
          lastPrice={asset?.latest_price?.price || 0}
        />
      </td>
      <td>{formatter.format(Math.round(100 * lastPrice) / 100)}</td>
      <td className={clsx(styles.percentTd, (delta < 0) && styles.percentTdRed)}>
        {`${(delta >= 0) ? '+' : '-'}${Math.abs(deltaPercent)}%`}
      </td>
      <td>
        {marketCup ? formatAssetStat(marketCup, true) : null}
      </td>
    </tr>
  )
}

function PopularStocksSkeleton({ title }: { title: string }) {
  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.h}>
          <div className={styles.title}>
            <IconArrowRight />
            {title ? (
              <span>{title}</span>
            ) : (
              <Skeleton style={{ width: '300px', display: 'inline-block' }} />
            )}
          </div>
        </div>
      </div>
      <table className={styles.assets}>
        <tbody>
          <tr>
            <th>Name</th>
            <th>1 Day</th>
            <th>Price</th>
            <th>24h</th>
            <th>Mrkt Cap</th>
          </tr>
        </tbody>
      </table>
      <div className={styles.assets}>
        {Array.from(Array(10).keys()).map((item: number) => (
          <Skeleton key={item} className={styles.itemSkeleton} />
        ))}
      </div>
    </PageContainer>
  )
}

function PopularStocks() {
  const [assets, setAssets] = useState<any>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const title = 'Popular Stocks'

  const navigate = useNavigate()

  const assetsData = useAssets()

  useEffect(() => {
    if (assetsData.isSuccess) {
      setLoaded(true)
      setAssets(assetsData?.data || [])
    }
  }, [assetsData.isSuccess])

  if (assetsData.isLoading && !loaded) {
    return (<PopularStocksSkeleton title={title} />)
  }

  return (
    <PageContainer className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.h} onClick={() => navigate(-1)}>
          <h1 className={styles.title}>
            <IconArrowRight />
            {title}
          </h1>
        </div>
      </div>
      <ScrollingCarousel className={styles.carousel}>
        <table className={clsx(
          styles.assets,
          assetsData.isLoading && styles.assets_loading,
        )}
        >
          <tbody>
            <tr>
              <th>Name</th>
              <th>1 Day</th>
              <th>Price</th>
              <th>24h</th>
              <th>Mrkt Cap</th>
            </tr>
            {assets.map((asset: any) => (asset.active ? (
              <Asset key={asset?.id} asset={asset} />
            ) : null))}
          </tbody>
        </table>
        <div />
      </ScrollingCarousel>
    </PageContainer>
  )
}

function PopularStocksPage() {
  return (
    <Page activeLink="/">
      <PopularStocks />
    </Page>
  )
}

export default PopularStocksPage
